
import { useState, useEffect } from 'react';

interface SubmitEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SubmitEntryModal({ isOpen, onClose }: SubmitEntryModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    reservationDate: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submissionDelay, setSubmissionDelay] = useState(0);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setSubmissionDelay(Date.now());
    } else {
      document.body.style.overflow = 'unset';
      setSubmitStatus('idle');
      setFormData({ name: '', email: '', phone: '', reservationDate: '' });
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 최소 2초 지연을 통한 스팸 방지
    const timeSinceOpen = Date.now() - submissionDelay;
    if (timeSinceOpen < 2000) {
      await new Promise(resolve => setTimeout(resolve, 2000 - timeSinceOpen));
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name.trim());
      formBody.append('email', formData.email.trim());
      formBody.append('phone', formData.phone.trim());
      formBody.append('reservationDate', formData.reservationDate);

      const response = await fetch('https://readdy.ai/api/form/d4en9e23fj18bu6hj1n0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok || response.status === 200) {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        const responseText = await response.text();
        console.error('Form submission failed:', response.status, responseText);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      <div className="relative bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 flex items-center justify-center w-10 h-10 text-neutral-400 hover:text-neutral-900 transition-colors duration-300 cursor-pointer z-10"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        <div className="p-12">
          <div className="text-center mb-10">
            <div className="inline-block w-16 h-px bg-neutral-300 mb-6"></div>
            <h2 className="text-3xl md:text-4xl mb-4 font-light tracking-tight text-neutral-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Submit Your Entry
            </h2>
            <p className="text-neutral-600 font-light">
              Please verify your existing reservation details
            </p>
          </div>

          {submitStatus === 'success' ? (
            <div className="text-center py-12">
              <div className="flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mx-auto mb-6">
                <i className="ri-check-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-2xl mb-3 font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                Entry Submitted
              </h3>
              <p className="text-neutral-600 font-light">
                Thank you! Your entry has been successfully submitted.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-readdy-form id="existing-client-entry">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm tracking-[0.1em] uppercase mb-3 font-medium text-neutral-900">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-6 py-4 border border-neutral-300 focus:border-neutral-900 focus:outline-none transition-colors duration-300 text-sm"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm tracking-[0.1em] uppercase mb-3 font-medium text-neutral-900">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-6 py-4 border border-neutral-300 focus:border-neutral-900 focus:outline-none transition-colors duration-300 text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm tracking-[0.1em] uppercase mb-3 font-medium text-neutral-900">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-6 py-4 border border-neutral-300 focus:border-neutral-900 focus:outline-none transition-colors duration-300 text-sm"
                    placeholder="+82 10 1234 5678"
                  />
                </div>

                <div>
                  <label className="block text-sm tracking-[0.1em] uppercase mb-3 font-medium text-neutral-900">
                    Reservation Date *
                  </label>
                  <input
                    type="date"
                    name="reservationDate"
                    required
                    value={formData.reservationDate}
                    onChange={(e) => setFormData({ ...formData, reservationDate: e.target.value })}
                    className="w-full px-6 py-4 border border-neutral-300 focus:border-neutral-900 focus:outline-none transition-colors duration-300 text-sm"
                  />
                </div>
              </div>

              {submitStatus === 'error' && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200">
                  <p className="text-sm text-red-600 font-light">
                    Something went wrong. Please try again.
                  </p>
                </div>
              )}

              <div className="mt-10">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-neutral-900 text-white text-sm tracking-[0.2em] uppercase font-medium hover:bg-neutral-800 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Entry'}
                </button>
              </div>

              <p className="text-xs text-neutral-500 text-center mt-6 font-light">
                By submitting this form, you confirm that the information provided is accurate and matches your existing reservation.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
